import { Box, Grid, Typography } from "@mui/material";
import { styled } from "@mui/system";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { BoxPaper } from "../../pages/PropertyDetails";
import {
  allowedToText,
  amenitiesConverter,
  constructionTypeConverter,
  energylabelConverter,
  excludedIncludedConverter,
  monthlyRentConverter,
  outdoorConverter,
  parkingConverter,
  rentalPeriodConverter,
  yesNoConverter,
} from "../../utils/enumConverter";
import PropertyDetailsSpecificationsField from "./PropertyDetailsSpecificationsField";
import { formatCurrency } from "../../utils/currency";

type PropertyDetailsSpecificationsProps = {
  property: any;
};

const PropertyDetailsSpecifications = ({
  property,
}: PropertyDetailsSpecificationsProps) => {
  const { t } = useTranslation();
  moment.locale("nl");

  const hasValidPropertyFeatures = (propertyFeatures: any): boolean => {
    if (!propertyFeatures) return false;

    return Object.values(propertyFeatures).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== 0;
    });
  };

  const formattedAvailableDate = property?.propertyDetails?.availableFromDate
    ? moment(property?.propertyDetails.availableFromDate).format("DD-MM-YYYY")
    : "";

  if (!hasValidPropertyFeatures(property?.propertyFeatures)) {
    return null;
  }

  return (
    <>
      <Grid item xs={12} marginTop={3}>
        <BoxPaper>
          <PropDetailsSpecifications
            sx={{ flexDirection: { xs: "column", md: "row" } }}
          >
            <Grid container>
              <Grid item xs={12} lg={6}>
                <Box sx={{ margin: "1rem 0" }}>
                  <Typography variant="h4">{t("details.apartment")}</Typography>
                </Box>
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.energy_label")}
                  border={false}
                  value={energylabelConverter(
                    property?.propertyFeatures?.energyLabel,
                  )}
                  skeletonWidth={40}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.living_area")}
                  border={true}
                  value={
                    property?.propertyFeatures?.livingArea &&
                    property?.propertyFeatures?.livingArea + " m²"
                  }
                  skeletonWidth={100}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.plot_area")}
                  border={true}
                  value={
                    property?.propertyFeatures?.plotArea &&
                    property?.propertyFeatures?.plotArea + " m²"
                  }
                  skeletonWidth={80}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.floors")}
                  border={true}
                  value={property?.propertyFeatures?.noOfFloors}
                  skeletonWidth={100}
                />

                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.rooms")}
                  border={true}
                  value={property?.propertyFeatures?.noOfRooms ?? ""}
                  skeletonWidth={30}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.bedrooms")}
                  border={true}
                  value={property?.propertyFeatures?.noOfBedrooms}
                  skeletonWidth={80}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.bathrooms")}
                  border={true}
                  value={property?.propertyFeatures?.noOfBathrooms}
                  skeletonWidth={100}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.bathroom_amenities")}
                  border={true}
                  value={amenitiesConverter(
                    property?.propertyFeatures?.bathroomAmenities?.join(", "),
                    t,
                  )}
                  skeletonWidth={60}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.toilet")}
                  border={true}
                  value={yesNoConverter(
                    property?.propertyFeatures?.separateToilet,
                    t,
                  )}
                  skeletonWidth={30}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.outdoor_space")}
                  border={true}
                  value={outdoorConverter(
                    property?.propertyFeatures?.outdoorSpace?.join(", "),
                    t,
                  )}
                  skeletonWidth={70}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.garage")}
                  border={true}
                  value={yesNoConverter(property?.propertyFeatures?.garage, t)}
                  skeletonWidth={70}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.shed")}
                  border={true}
                  value={yesNoConverter(
                    property?.propertyFeatures?.outdoorStorage,
                    t,
                  )}
                  skeletonWidth={40}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.parking")}
                  border={true}
                  value={parkingConverter(
                    property?.propertyFeatures?.parkingTypes?.join(", "),
                    t,
                  )}
                  skeletonWidth={50}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.construction")}
                  border={true}
                  value={constructionTypeConverter(
                    property?.propertyFeatures?.constructionType,
                    t,
                  )}
                  skeletonWidth={90}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <Box sx={{ margin: "1rem 0" }}>
                  <Typography variant="h4">
                    {t("details.rental_conditions")}
                  </Typography>
                </Box>

                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.date_available")}
                  border={false}
                  value={formattedAvailableDate}
                  skeletonWidth={90}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.rental_period")}
                  border={true}
                  value={rentalPeriodConverter(
                    property?.propertyDetails?.contractDuration,
                    t,
                  )}
                  skeletonWidth={50}
                />

                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.deposit")}
                  border={true}
                  value={monthlyRentConverter(
                    property?.propertyFeatures?.depositAmount,
                    t,
                  )}
                  skeletonWidth={60}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.service_charge")}
                  border={true}
                  value={
                    property?.propertyFeatures?.serviceCharge &&
                      formatCurrency(property?.propertyFeatures?.serviceCharge)
                  }
                  skeletonWidth={30}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.heating_cost")}
                  border={true}
                  value={excludedIncludedConverter(
                    property?.propertyFeatures?.utilities,
                    t,
                  )}
                  skeletonWidth={80}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.tv_internet")}
                  border={true}
                  value={excludedIncludedConverter(
                    property?.propertyFeatures?.televisionAndInternet,
                    t,
                  )}
                  skeletonWidth={100}
                />

                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.furnished_decorated")}
                  border={true}
                  value={yesNoConverter(
                    property?.propertyFeatures?.furnishedDecorated,
                    t,
                  )}
                  skeletonWidth={40}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.furniture")}
                  border={true}
                  value={yesNoConverter(
                    property?.propertyFeatures?.includingFurniture,
                    t,
                  )}
                  skeletonWidth={50}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.sharing")}
                  border={true}
                  value={allowedToText(
                    property?.propertyFeatures?.houseShare,
                    t,
                  )}
                  skeletonWidth={80}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.pets")}
                  border={true}
                  value={allowedToText(property?.propertyFeatures?.pets, t)}
                  skeletonWidth={30}
                />
                <PropertyDetailsSpecificationsField
                  property={property}
                  label={t("specifications.smoking")}
                  border={true}
                  value={allowedToText(property?.propertyFeatures?.smoking, t)}
                  skeletonWidth={40}
                />
              </Grid>
            </Grid>
          </PropDetailsSpecifications>
        </BoxPaper>
      </Grid>
    </>
  );
};

const PropDetailsSpecifications = styled("ul")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",

  div: {
    li: {
      width: "90%",
    },
  },
});

export default PropertyDetailsSpecifications;
